import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../state/auth'

/**
 * The account area at the bottom-left of the sidebar.
 *
 * Signed in, it shows the REAL customer's name and account number from their Cashit account - not a
 * nickname held by this site, because SellGPT has no accounts of its own. Signed out, it says so plainly
 * and explains when a number will be asked for, so nobody is surprised by an OTP screen halfway through.
 */
export default function AccountArea({ onSignIn }: { onSignIn: () => void }) {
  const { customer, loading, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const container = useRef<HTMLDivElement>(null)

  // A menu that only closes on its own button is a menu that stays open behind whatever is clicked next.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const initial = (customer?.name?.trim()?.[0] ?? 'G').toUpperCase()
  const name = loading ? 'Loading…' : (customer?.name?.trim() || (customer ? 'Cashit customer' : 'Guest'))
  const detail = customer
    ? customer.customerNo
      ? `${customer.mobileNumber} · ${customer.customerNo}`
      : customer.mobileNumber
    : 'Not signed in'

  return (
    <div className="account-area" ref={container}>
      <div className="account-menu" id="accountMenu" hidden={!open}>
        {customer ? (
          <>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/quotes')
              }}
            >
              My sales
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                signOut()
              }}
            >
              Sign out
            </button>
            <p>Signed in to your Cashit account. Your sales appear on cashit.in too.</p>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                onSignIn()
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/')
              }}
            >
              Start a sale
            </button>
            <p>
              You can explore and get a Cashit price as a guest. Sign in to see sales you have already
              placed, or to use an address Cashit already has.
            </p>
          </>
        )}
      </div>

      {/* SIGNED OUT, THE WHOLE ROW IS THE SIGN-IN BUTTON.
          It used to open a menu, which meant the only way to sign in was to guess that "Guest" was a
          menu and open it - so a returning customer had, in effect, no way in at all. Signed in, the
          same row keeps the menu, because then it has more than one thing to offer. */}
      {!customer && !loading ? (
        <button className="account-button sign-in" type="button" onClick={onSignIn}>
          <span className="avatar" aria-hidden="true">
            {initial}
          </span>
          <span className="account-name">
            <strong>Sign in</strong>
            <small>Mobile number and a one-time code</small>
          </span>
          <span className="account-dots" aria-hidden="true">
            →
          </span>
        </button>
      ) : (
      <button
        className="account-button"
        id="accountToggle"
        type="button"
        aria-expanded={open}
        aria-controls="accountMenu"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="account-name">
          <strong>{name}</strong>
          <small>{detail}</small>
        </span>
        <span className="account-dots" aria-hidden="true">
          ⋯
        </span>
      </button>
      )}
    </div>
  )
}
